"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var constants_1 = require("@services/report-state/constants");
var widgets_1 = require("@shared/constants/widgets");
var angular_1 = require("angular");
var lodash_1 = __importDefault(require("lodash"));
var WIDGET_TYPES_TO_EXCLUDE_FROM_SECTION_LIMIT = [
    widgets_1.PR_WIDGETS.PAGE_BREAK,
    widgets_1.PR_WIDGETS.BLANK_SPACE,
    widgets_1.PR_WIDGETS.DIVIDER
];
/**
 * Helper functions for getting info and updating widgets
 * as they are stored on the client. Prefer using methods
 * defined here instead of directly manipulating a widget object.
 */
function WidgetUtilsFactory(activeReport, sectionManager, widgetLimitService, 
// @ts-ignore
eventService, 
// @ts-ignore
EVENTS, 
// @ts-ignore
autoHeightHelper) {
    return {
        getWidgets: getWidgets,
        getWidget: getWidget,
        getWidgetIndex: getWidgetIndex,
        isFixedHeightWidget: isFixedHeightWidget,
        getPageIdAtInsertIndex: getPageIdAtInsertIndex,
        getWidgetsCount: getWidgetsCount,
        hasReachedWidgetLimit: hasReachedWidgetLimit,
        canWidgetsBeAddedToReport: canWidgetsBeAddedToReport,
        canWidgetsBeAddedToSection: canWidgetsBeAddedToSection,
        updateWidgetFixedHeightOption: updateWidgetFixedHeightOption,
        updateWidgetPadding: updateWidgetPadding,
        updateWidgetStyle: updateWidgetStyle,
        validateWidgetLayout: validateWidgetLayout,
        startMoving: startMoving,
        stopMoving: stopMoving
    };
    function getWidgets() {
        return sectionManager.getWidgets();
    }
    function getWidget(id) {
        // @ts-ignore
        return lodash_1.default.find(getWidgets(), { _id: id });
    }
    function getWidgetIndex(w, widgets) {
        return lodash_1.default.findIndex(widgets, function (widget) {
            return widget._id === w._id;
        });
    }
    /**
     * Returns the page id that a widget would be on
     * if it were inserted at `index` in the given widgets array.
     * This assumes that the widget to insert is not a page break.
     */
    function getPageIdAtInsertIndex(index, allWidgets) {
        if (index === 0) {
            return lodash_1.default.get(activeReport.getPages(), '0._id');
        }
        // Because the widget is not a page break,
        // the widget will be in the same section as the preceding widget
        var widgetInDestSection = allWidgets[index - 1];
        return activeReport.getPageIdOfWidget(widgetInDestSection);
    }
    function getWidgetsCount() {
        var widgets = getWidgets() || [];
        return widgets.length;
    }
    function canWidgetsBeAddedToReport(numWidgetsToAdd) {
        var widgetCountLimit = widgetLimitService.getWidgetLimit();
        return !widgetCountLimit || getWidgetsCount() + numWidgetsToAdd <= widgetCountLimit;
    }
    function canWidgetsBeAddedToSection(pageId, widgetTypes) {
        var numNewWidgets = countWidgetsForSectionLimit(widgetTypes);
        if (numNewWidgets === 0) {
            // users should be able to add pagebreaks, blank space, etc.
            // to a section that has already exceeded the limit
            return true;
        }
        var widgetTypesInSection = getWidgetTypesInSection(pageId);
        var numExistingWidgets = countWidgetsForSectionLimit(widgetTypesInSection);
        return numNewWidgets + numExistingWidgets <= constants_1.WIDGET_LIMIT_PER_SECTION;
    }
    function hasReachedWidgetLimit() {
        var widgetCountLimit = widgetLimitService.getWidgetLimit();
        return !!widgetCountLimit && widgetCountLimit <= getWidgetsCount();
    }
    // @ts-ignore
    function updateWidgetPadding(widgetId, padding) {
        var widget = getWidget(widgetId);
        // @ts-ignore
        if (!lodash_1.default.isEqual(widget.layout.padding, padding)) {
            // @ts-ignore
            widget.layout.padding = padding;
            sectionManager.saveWidget(widget);
        }
    }
    /**
     * Checks if an auto height widget is using the fixed height mode
     */
    function isFixedHeightWidget(widgetId) {
        var widget = getWidget(widgetId);
        // @ts-ignore
        return autoHeightHelper.isAutoHeightOptional(widget === null || widget === void 0 ? void 0 : widget._type) && (widget === null || widget === void 0 ? void 0 : widget.layout.useFixedHeight);
    }
    function updateWidgetFixedHeightOption(widgetId, useFixedHeight) {
        var widget = getWidget(widgetId);
        // @ts-ignore
        if (widget.layout.useFixedHeight !== useFixedHeight) {
            // @ts-ignore
            widget.layout.useFixedHeight = useFixedHeight;
            sectionManager.saveWidget(widget);
        }
    }
    // @ts-ignore
    function updateWidgetStyle(widgetId, style) {
        var widget = getWidget(widgetId);
        style = stripUndefinedValues(style);
        // @ts-ignore
        if (!lodash_1.default.isEqual(widget.layout.style, style)) {
            // @ts-ignore
            widget.layout.style = style;
            sectionManager.saveWidget(widget);
        }
    }
    function validateWidgetLayout(widgetId) {
        var widget = getWidget(widgetId);
        if (!widget || !widget.layout) {
            return;
        }
        var layoutKeys = ['row', 'col', 'rowSpan', 'colSpan'];
        var changed = false;
        lodash_1.default.forEach(layoutKeys, function (key) {
            // non-integer layout values break ripsaw
            // @ts-ignore
            // @ts-ignore
            var value = widget.layout[key];
            var rounded = Math.round(value);
            if (rounded !== value) {
                // @ts-ignore
                // @ts-ignore
                widget.layout[key] = rounded;
                changed = true;
            }
        });
        if (changed) {
            sectionManager.saveWidget(widget);
        }
    }
    function startMoving(widget) {
        widget.moving = true;
        if (widget.chunks) {
            lodash_1.default.each(widget.chunks, startMoving);
        }
    }
    function stopMoving(widget) {
        delete widget.moving;
        if (widget.chunks) {
            lodash_1.default.each(widget.chunks, stopMoving);
        }
    }
    // @ts-ignore
    function stripUndefinedValues(obj) {
        return lodash_1.default.pickBy(obj, function (value) { return !lodash_1.default.isUndefined(value); });
    }
    function getWidgetTypesInSection(pageId) {
        var page = activeReport.getPage(pageId) || {};
        // @ts-ignore
        return lodash_1.default.map(page.widgets, '_type');
    }
    function countWidgetsForSectionLimit(widgetTypes) {
        return lodash_1.default.difference(widgetTypes, WIDGET_TYPES_TO_EXCLUDE_FROM_SECTION_LIMIT).length;
    }
}
(function (angularModule) {
    angularModule.factory('pr.widgetUtils', [
        'prActiveReport',
        'prSectionManager',
        'prWidgetLimitService',
        'prEventService',
        'pr.EVENTS',
        'prAutoHeightHelper',
        WidgetUtilsFactory
    ]);
})(angular_1.module('qualtrics.pr'));
